import { useEffect, useState } from "react";
import { MdAdd, MdDelete, MdEdit } from "react-icons/md";
import { toast } from "react-toastify";
import AddBlogModal from "./AddBlogModal";
import UpdateBlogModal from "./UpdateBlogModal";
import { adminApiService } from "services/api";

export default function BlogTable() {
  const [showAddBlog, setShowAddBlog] = useState(false);
  const [showEditBlog, setShowEditBlog] = useState(false);
  const [blogs, setBlogs] = useState([]);
  const [editableBlog, setEditableBlog] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const toggleAddBlog = () => {
    setShowAddBlog(!showAddBlog);
  };

  const toggleEditBlog = (blog) => {
    setEditableBlog(blog);
    setShowEditBlog(!showEditBlog);
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const handleDeleteBlog = async (blogId) => {
    if (window.confirm("Are you sure you want to delete this blog?")) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/blogs/delete-blog/${blogId}`,
          { method: "DELETE" }
        );
        if (!response.ok) throw new Error("Failed to delete the blog.");
        toast.success("Blog deleted successfully!");
        fetchBlogs();
      } catch (error) {
        console.error("Delete error:", error);
        toast.error("Error deleting the blog.");
      }
    }
  };

  const fetchBlogs = async () => {
    setIsLoading(true);
    try {
      const response = await adminApiService.getBlogs();

      if (!response.ok) throw new Error("Failed to fetch blogs.");
      const data = await response.json();

      setBlogs(data.allBlogs);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Error fetching blogs.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-10 rounded-2xl bg-white p-8 shadow-sm">
          <div className="flex flex-col items-center justify-between space-y-4 sm:flex-row sm:space-y-0">
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Blog & News</h1>
              <p className="mt-3 text-base text-gray-600">
                Manage your blog posts and news articles
              </p>
            </div>
            <button
              className="inline-flex items-center rounded-xl bg-primary px-6 py-3 text-base font-medium text-white shadow-sm transition-all hover:bg-primaryDark hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              onClick={toggleAddBlog}
            >
              <MdAdd className="mr-2" size={24} />
              Add Blog & News
            </button>
          </div>
        </div>

        {showAddBlog && (
          <AddBlogModal
            toggleAddCategory={toggleAddBlog}
            showAddCategory={showAddBlog}
            onBlogAdded={fetchBlogs}
          />
        )}

        {showEditBlog && editableBlog && (
          <UpdateBlogModal
            blogId={editableBlog.id}
            toggleModal={() => toggleEditBlog(null)}
            showModal={showEditBlog}
            onBlogUpdated={fetchBlogs}
          />
        )}

        {/* Blog Table */}
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-primary">
              <tr>
                <th
                  scope="col"
                  className="px-8 py-4 text-left text-sm font-medium uppercase tracking-wider text-white"
                >
                  Image
                </th>
                <th
                  scope="col"
                  className="px-8 py-4 text-left text-sm font-medium uppercase tracking-wider text-white"
                >
                  Title
                </th>
                <th
                  scope="col"
                  className="px-8 py-4 text-left text-sm font-medium uppercase tracking-wider text-white"
                >
                  Link
                </th>
                <th
                  scope="col"
                  className="px-8 py-4 text-right text-sm font-medium uppercase tracking-wider text-white"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {isLoading ? (
                <tr>
                  <td colSpan="4" className="px-8 py-12 text-center">
                    <div className="flex justify-center">
                      <div className="border-t-transparent h-12 w-12 animate-spin rounded-full border-4 border-primary"></div>
                    </div>
                  </td>
                </tr>
              ) : blogs.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-8 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="rounded-full bg-primary/10 p-6">
                        <MdAdd className="h-16 w-16 text-primary" />
                      </div>
                      <h3 className="mt-6 text-xl font-semibold text-gray-900">
                        No Blogs Available
                      </h3>
                      <p className="mt-3 text-base text-gray-500">
                        Get started by adding your first blog post.
                      </p>
                      <button
                        onClick={toggleAddBlog}
                        className="mt-8 inline-flex items-center rounded-xl bg-primary px-6 py-3 text-base font-medium text-white shadow-sm transition-all hover:bg-primaryDark hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                      >
                        <MdAdd className="mr-2" size={24} />
                        Add Blog & News
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                blogs.map((blog) => (
                  <tr
                    key={blog.id}
                    className="transition-colors duration-200 hover:bg-gray-50"
                  >
                    <td className="px-8 py-6">
                      <img
                        src={`${process.env.REACT_APP_API_URL_Plain}/config/uploads/blogs/${blog.image}`}
                        alt="Blog"
                        className="h-16 w-16 rounded-xl object-cover"
                      />
                    </td>
                    <td className="whitespace-nowrap px-8 py-6">
                      <div className="flex items-center">
                        <div className="text-base font-medium text-gray-900">
                          {blog.title}
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-8 py-6">
                      <div className="flex items-center">
                        <a
                          href={blog.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-base font-medium text-primary hover:text-primaryDark"
                        >
                          {blog.link}
                        </a>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-8 py-6 text-right text-sm font-medium">
                      <div className="flex justify-end space-x-3">
                        <button
                          onClick={() => toggleEditBlog(blog)}
                          className="rounded-xl p-2.5 text-primary transition-colors hover:bg-primary/10"
                          title="Edit"
                        >
                          <MdEdit size={24} />
                        </button>
                        <button
                          onClick={() => handleDeleteBlog(blog.id)}
                          className="rounded-xl p-2.5 text-red-600 transition-colors hover:bg-red-50"
                          title="Delete"
                        >
                          <MdDelete size={24} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
